<template>
  <div class="adn-authorize-page">
    <div class="adn-authorize-card">
      <h1 class="adn-title">
        Xác nhận chia sẻ thông tin để đăng nhập hệ thống
        <strong>{{ clientConfig.name }}</strong>
      </h1>
      <div class="adn-flow">
        <div class="adn-avatar-wrap">
          <img v-if="userAvatar" :src="userAvatar" alt="" class="adn-avatar" />
          <div v-else class="adn-avatar adn-avatar--placeholder">{{ userInitial }}</div>
        </div>
        <div class="adn-flow-line">
          <span class="adn-shield">🛡</span>
        </div>
        <div class="adn-app-logo" :class="`adn-app-logo--${clientConfig.logoVariant}`">
          <div class="adn-app-icon">{{ clientConfig.iconEmoji }}</div>
        </div>
      </div>
      <p class="adn-app-name">{{ clientConfig.name }}</p>

      <p class="adn-timer">
        Thời gian hết hạn chia sẻ
        <strong class="text-danger">{{ countdown }}</strong>
      </p>

      <p class="adn-desc">{{ clientConfig.description }}</p>

      <button type="button" class="adn-toggle-info" @click="showInfo = !showInfo">
        <i :class="showInfo ? 'far fa-eye-slash' : 'far fa-eye'"></i>
        {{ showInfo ? 'Ẩn thông tin' : 'Hiện thông tin' }}
      </button>

      <div class="adn-info-list">
        <p class="adn-info-title">Các thông tin chia sẻ bắt buộc</p>
        <div class="adn-info-row">
          <span>Mã nhân sự</span>
          <strong>{{ showInfo ? employeeCode : masked }}</strong>
        </div>
        <div class="adn-info-row">
          <span>Họ và tên</span>
          <strong>{{ showInfo ? userName : masked }}</strong>
        </div>
        <div class="adn-info-row">
          <span>Email</span>
          <strong>{{ showInfo ? userEmail : masked }}</strong>
        </div>
        <div class="adn-info-row">
          <span>Phòng ban</span>
          <strong>{{ showInfo ? userDepartment : masked }}</strong>
        </div>
        <div class="adn-info-row">
          <span>Loại tài khoản</span>
          <strong>{{ showInfo ? accountType : masked }}</strong>
        </div>
      </div>

      <div class="adn-purpose">
        <p class="adn-info-title">Mục đích chia sẻ, xử lý dữ liệu</p>
        <p class="adn-purpose-text">{{ clientConfig.purpose }}</p>
      </div>

      <label class="adn-consent">
        <input v-model="agreed" type="checkbox" :disabled="isExpired" />
        <span>
          Tôi đã đọc và hiểu rõ nội dung mục đích chia sẻ, xử lý dữ liệu nêu trên và đồng ý
          với các nội dung này.
        </span>
      </label>

      <p v-if="error" class="adn-error">{{ error }}</p>

      <button
        type="button"
        class="btn btn-danger btn-lg btn-block adn-submit"
        :class="{ 'adn-submit--ello': clientConfig.logoVariant === 'ello' }"
        :disabled="!agreed || submitting || isExpired"
        @click="confirmAuthorize"
      >
        {{ submitting ? 'Đang xử lý...' : 'Đồng ý và đăng nhập' }}
      </button>

      <button type="button" class="btn btn-link btn-block adn-cancel" @click="cancelAuthorize">
        Hủy
      </button>
    </div>
  </div>
</template>

<script>
import { mapGetters } from "vuex";
import { ADN_OAUTH_AUTHORIZE } from "@/core/services/store/auth.module";
import {
  DEFAULT_OAUTH_CLIENT_ID,
  resolveOAuthClient,
} from "@/core/config/oauthClients";

const CODE_TTL = 300;

export default {
  name: "AdnAuthorize",
  data() {
    return {
      showInfo: false,
      agreed: false,
      submitting: false,
      error: "",
      masked: "******",
      expiresAt: Date.now() + CODE_TTL * 1000,
      countdown: "05:00",
      timerId: null,
      isExpired: false,
    };
  },
  computed: {
    ...mapGetters(["currentUser"]),
    userName() {
      return this.currentUser?.name || "";
    },
    userEmail() {
      return this.currentUser?.email || "";
    },
    userAvatar() {
      return this.currentUser?.image || "";
    },
    userInitial() {
      const name = this.userName || "?";
      return name.charAt(0).toUpperCase();
    },
    employeeCode() {
      const id = this.currentUser?.id;
      return id ? String(id + 100000) : "";
    },
    userDepartment() {
      return this.currentUser?.profile?.branch?.name
        || this.currentUser?.branch
        || "";
    },
    accountType() {
      return this.currentUser?.account_type?.name || "";
    },
    redirectUri() {
      return this.$route.query.redirect_uri || "";
    },
    state() {
      return this.$route.query.state || "";
    },
    clientId() {
      return this.$route.query.client_id || DEFAULT_OAUTH_CLIENT_ID;
    },
    clientConfig() {
      return resolveOAuthClient(this.clientId) || resolveOAuthClient(DEFAULT_OAUTH_CLIENT_ID);
    },
  },
  mounted() {
    if (!resolveOAuthClient(this.clientId)) {
      this.error = "Ứng dụng yêu cầu đăng nhập không hợp lệ.";
      return;
    }

    if (!this.currentUser || !this.currentUser.id) {
      const returnUrl = this.$route.fullPath;
      return this.$router.replace({ path: "/login", query: { returnUrl } });
    }

    if (!this.redirectUri) {
      this.error = "Thiếu tham số redirect_uri.";
      return;
    }

    this.startCountdown();
  },
  beforeDestroy() {
    if (this.timerId) clearInterval(this.timerId);
  },
  methods: {
    startCountdown() {
      const tick = () => {
        const remain = Math.max(0, this.expiresAt - Date.now());
        const mm = String(Math.floor(remain / 60000)).padStart(2, "0");
        const ss = String(Math.floor((remain % 60000) / 1000)).padStart(2, "0");
        this.countdown = `${mm}:${ss}`;
        if (remain <= 0) {
          clearInterval(this.timerId);
          this.isExpired = true;
          this.error = this.clientConfig.expiredMessage;
        }
      };
      tick();
      this.timerId = setInterval(tick, 1000);
    },
    async confirmAuthorize() {
      if (!this.agreed || this.submitting || this.isExpired) return;
      this.error = "";
      this.submitting = true;
      try {
        const data = await this.$store.dispatch(ADN_OAUTH_AUTHORIZE, {
          client_id: this.clientId,
          redirect_uri: this.redirectUri,
          state: this.state,
        });
        const code = data?.data?.code;
        if (!code) {
          this.error = data?.message || "Không tạo được mã xác thực.";
          return;
        }
        const url = new URL(this.redirectUri);
        url.searchParams.set("code", code);
        if (this.state) url.searchParams.set("state", this.state);
        window.location.href = url.toString();
      } catch (err) {
        this.error =
          err?.data?.message
          || "Không thể xác nhận chia sẻ thông tin. Vui lòng thử lại.";
      } finally {
        this.submitting = false;
      }
    },
    cancelAuthorize() {
      if (!this.redirectUri) {
        this.error = "Thiếu tham số redirect_uri.";
        return;
      }
      const url = new URL(this.redirectUri);
      url.searchParams.set("error", "access_denied");
      if (this.state) url.searchParams.set("state", this.state);
      window.location.href = url.toString();
    },
  },
};
</script>

<style scoped>
.adn-authorize-page {
  min-height: 100vh;
  background: #f3f4f6;
  display: flex;
  justify-content: center;
  padding: 24px 16px 40px;
}

.adn-authorize-card {
  width: 100%;
  max-width: 480px;
  background: #fff;
  border-radius: 16px;
  box-shadow: 0 8px 30px rgba(0, 0, 0, 0.08);
  padding: 24px 20px 28px;
}

.adn-title {
  font-size: 1.05rem;
  line-height: 1.5;
  text-align: center;
  margin-bottom: 20px;
  color: #1f2937;
}

.adn-flow {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 12px;
  margin-bottom: 8px;
}

.adn-avatar-wrap,
.adn-app-logo {
  width: 72px;
  height: 72px;
  flex-shrink: 0;
}

.adn-avatar {
  width: 72px;
  height: 72px;
  border-radius: 50%;
  object-fit: cover;
  border: 3px solid #e5e7eb;
}

.adn-avatar--placeholder {
  display: flex;
  align-items: center;
  justify-content: center;
  background: #8860d0;
  color: #fff;
  font-size: 1.5rem;
  font-weight: 700;
}

.adn-flow-line {
  flex: 1;
  max-width: 80px;
  height: 2px;
  background: repeating-linear-gradient(90deg, #d4a017 0, #d4a017 6px, transparent 6px, transparent 12px);
  position: relative;
}

.adn-shield {
  position: absolute;
  left: 50%;
  top: 50%;
  transform: translate(-50%, -50%);
  background: #fff;
  padding: 0 4px;
  font-size: 1.1rem;
}

.adn-app-logo {
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 50%;
  border: 3px solid #d4a017;
}

.adn-app-logo--adn {
  background: linear-gradient(135deg, #1a1f3a, #2d3561);
}

.adn-app-logo--ello {
  background: linear-gradient(135deg, #472f92, #5a43a8);
  border-color: #5a43a8;
}

.adn-app-icon {
  font-size: 2rem;
}

.adn-app-name {
  text-align: center;
  font-weight: 700;
  color: #374151;
  margin-bottom: 12px;
}

.adn-timer {
  text-align: center;
  font-size: 0.9rem;
  color: #6b7280;
  margin-bottom: 16px;
}

.adn-desc {
  font-size: 0.9rem;
  color: #4b5563;
  margin-bottom: 12px;
}

.adn-toggle-info {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  border: 1px solid #d1d5db;
  background: #fff;
  border-radius: 999px;
  padding: 6px 14px;
  font-size: 0.85rem;
  margin-bottom: 14px;
  cursor: pointer;
}

.adn-info-list,
.adn-purpose {
  background: #f9fafb;
  border: 1px solid #e5e7eb;
  border-radius: 12px;
  padding: 14px;
  margin-bottom: 14px;
}

.adn-info-title {
  font-weight: 700;
  font-size: 0.9rem;
  margin-bottom: 10px;
  color: #111827;
}

.adn-info-row {
  display: flex;
  justify-content: space-between;
  gap: 12px;
  padding: 8px 0;
  border-bottom: 1px solid #eceff3;
  font-size: 0.88rem;
}

.adn-info-row:last-child {
  border-bottom: none;
}

.adn-purpose-text {
  font-size: 0.88rem;
  color: #4b5563;
  margin: 0;
  line-height: 1.5;
}

.adn-consent {
  display: flex;
  gap: 10px;
  align-items: flex-start;
  font-size: 0.85rem;
  color: #374151;
  margin-bottom: 16px;
  cursor: pointer;
}

.adn-consent input {
  margin-top: 3px;
}

.adn-error {
  color: #dc2626;
  font-size: 0.88rem;
  margin-bottom: 12px;
}

.adn-submit {
  border-radius: 999px;
  font-weight: 700;
  margin-bottom: 8px;
}

.adn-submit--ello {
  background: linear-gradient(135deg, #472f92, #5a43a8);
  border-color: #5a43a8;
}

.adn-submit--ello:hover,
.adn-submit--ello:focus {
  background: linear-gradient(135deg, #3d287d, #4f3996);
  border-color: #4f3996;
}

.adn-cancel {
  color: #6b7280;
}
</style>
